import React, { useEffect, useRef, useState } from 'react';

/**
 * Modern animation component that uses Intersection Observer and CSS transforms
 * Replaces react-reveal with lightweight, performant animations
 */
export const AnimateOnScroll = ({ 
  children, 
  direction = 'up', 
  delay = 0,
  duration = 600,
  distance = 30,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Optional: unobserve after animation to improve performance
          observer.unobserve(entry.target);
        }
      },
      { 
        threshold: 0.1, // Trigger when 10% of element is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before element enters viewport
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  const getTransform = () => {
    if (!isVisible) {
      switch (direction) {
        case 'left':
          return 'translateX(-' + distance + 'px)';
        case 'right':
          return 'translateX(' + distance + 'px)';
        case 'up':
          return 'translateY(' + distance + 'px)';
        case 'down':
          return 'translateY(-' + distance + 'px)';
        case 'zoom':
          return 'scale(0.92)';
        default:
          return 'translateY(' + distance + 'px)';
      }
    }
    // When visible, return to original position/scale
    return direction === 'zoom' ? 'scale(1)' : 'translate(0, 0)';
  };

  const style = {
    opacity: isVisible ? 1 : 0,
    transform: getTransform(),
    transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
    willChange: isVisible ? 'auto' : 'transform, opacity'
  };

  return (
    <div ref={ref} style={style} className={className}>
      {children}
    </div>
  );
};

export default AnimateOnScroll;
